export const AGENT_FILTER_ALL = 'all';

export const DEFAULT_SORT_OPTION = 'name-asc';

export const DEFAULT_EMPTY_POSITION_LABEL = 'Agent';

export const AGENT_SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name A–Z' },
  { value: 'name-desc', label: 'Name Z–A' },
  { value: 'agency-asc', label: 'Agency A–Z' }
];

// default field readers, matching the agent shape returned by /agencies/selectedagents
const DEFAULT_ACCESSORS = {
  name: agent => `${agent.First_Name ?? ''} ${agent.Last_Name ?? ''}`,
  email: agent => agent.Email,
  npn: agent => agent.NPN,
  agency: agent => agent.Agency,
  position: agent => agent.Position_at_Agency
};

function readText(value) {
  return String(value ?? '').trim();
}

function resolveAccessors(accessors) {
  return { ...DEFAULT_ACCESSORS, ...accessors };
}

// blank positions are offered and matched under the "Agent" label
function matchesPosition(rawPosition, positionFilter) {
  const position = readText(rawPosition);
  if (positionFilter === DEFAULT_EMPTY_POSITION_LABEL) {
    return position === '' || position === DEFAULT_EMPTY_POSITION_LABEL;
  }
  return position === positionFilter;
}

function matchesQuery(agent, read, query) {
  const textFields = [read.name(agent), read.email(agent), read.agency(agent)];
  const matchesText = textFields.some(field =>
    String(field ?? '')
      .toLowerCase()
      .includes(query)
  );
  if (matchesText) return true;
  return String(read.npn(agent) ?? '').includes(query);
}

function compareAgents(a, b, read, sortOption) {
  if (sortOption === 'agency-asc') {
    return String(read.agency(a) ?? '').localeCompare(
      String(read.agency(b) ?? '')
    );
  }
  const nameA = readText(read.name(a)).toLocaleLowerCase();
  const nameB = readText(read.name(b)).toLocaleLowerCase();
  if (sortOption === 'name-desc') return nameB.localeCompare(nameA);
  return nameA.localeCompare(nameB);
}

/**
 * builds the Position filter options available for a list of agents
 * @param agents - rows the Position dropdown should offer options for
 * @param accessors - optional field readers, for rows that don't use the default agent shape
 */
export function getAgentPositionOptions(agents, accessors) {
  const read = resolveAccessors(accessors);
  const unique = new Set();
  let hasBlankPosition = false;

  (agents ?? []).forEach(agent => {
    const position = readText(read.position(agent));
    if (position) unique.add(position);
    else hasBlankPosition = true;
  });

  const options = [...unique].sort((a, b) => a.localeCompare(b));
  if (hasBlankPosition && !unique.has(DEFAULT_EMPTY_POSITION_LABEL)) {
    options.unshift(DEFAULT_EMPTY_POSITION_LABEL);
  }
  return options;
}

/**
 * applies the shared agents toolbar filters (search, position, sort) to a list of rows
 * @param agents - rows to filter, left untouched
 * @param searchQuery - free text matched against name, email, NPN and agency
 * @param positionFilter - a position label, or AGENT_FILTER_ALL to keep every row
 * @param sortOption - one of AGENT_SORT_OPTIONS values
 * @param accessors - optional field readers, for rows that don't use the default agent shape
 */
export function filterAndSortAgents({
  agents,
  searchQuery = '',
  positionFilter = AGENT_FILTER_ALL,
  sortOption = DEFAULT_SORT_OPTION,
  accessors
}) {
  const read = resolveAccessors(accessors);
  let list = [...(agents ?? [])];

  if (positionFilter !== AGENT_FILTER_ALL) {
    list = list.filter(agent =>
      matchesPosition(read.position(agent), positionFilter)
    );
  }

  const query = searchQuery.trim().toLowerCase();
  if (query) {
    list = list.filter(agent => matchesQuery(agent, read, query));
  }

  list.sort((a, b) => compareAgents(a, b, read, sortOption));
  return list;
}
